import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { AssessmentStatus, RiskLevel } from "../api/types";

export type Selection = "A" | "B" | "custom" | null;

export interface PerRowState {
  selected: Selection;
  comment: string;
  customDesc: string;
  customComm: string;
  finalRisk: RiskLevel;
  finalAssessmentStatus: AssessmentStatus;
  sourceOutputId: string | null;
  approved: boolean;
}

interface WizardState {
  activeAnalysisId: string | null;
  rowStates: Record<string, PerRowState>;
  setActiveAnalysis: (analysisId: string) => void;
  setRow: (rowKey: string, patch: Partial<PerRowState>) => void;
  reset: () => void;
}

const defaultRowState: PerRowState = {
  selected: null,
  comment: "",
  customDesc: "",
  customComm: "",
  finalRisk: "none",
  finalAssessmentStatus: "cannot_assess",
  sourceOutputId: null,
  approved: false,
};

export const useWizardStore = create<WizardState>()(
  persist(
    (set, get) => ({
      activeAnalysisId: null,
      rowStates: {},
      // Switching to a new analysis wipes prior approvals so they don't leak
      // across uploads. (Re-entering the same analysis preserves drafts.)
      setActiveAnalysis: (analysisId) => {
        if (get().activeAnalysisId === analysisId) return;
        set({ activeAnalysisId: analysisId, rowStates: {} });
      },
      setRow: (rowKey, patch) =>
        set((s) => ({
          rowStates: {
            ...s.rowStates,
            [rowKey]: { ...defaultRowState, ...s.rowStates[rowKey], ...patch },
          },
        })),
      reset: () => set({ activeAnalysisId: null, rowStates: {} }),
    }),
    { name: "ensovia-wizard" },
  ),
);
